import React, { createContext, useState, useContext, useCallback, useMemo, useEffect } from 'react';

/**
 * BaseFilterContext - Generic filter state management
 * Provides reusable filter operations and state management
 */
const BaseFilterContext = createContext();

export const useBaseFilter = () => useContext(BaseFilterContext);

export const BaseFilterProvider = ({ children, entityType = 'generic' }) => {
  // ============================================================================
  // FILTER STATE MANAGEMENT
  // ============================================================================
  
  // Search terms for different fields
  const [searchByKeyword, setSearchByKeyword] = useState("");
  const [searchByTitle, setSearchByTitle] = useState("");
  const [searchByDescription, setSearchByDescription] = useState("");
  const [searchByTag, setSearchByTag] = useState("");
  
  // Date filtering configuration
  const [dateFilterMode, setDateFilterMode] = useState("any");
  const [queryFromDay, setQueryFromDay] = useState("");
  const [queryToDay, setQueryToDay] = useState("");
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  
  // Multi-select filters
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedVisibilities, setSelectedVisibilities] = useState([]);
  
  // Flag to track if any filters are currently active
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // ============================================================================
  // FILTER MANAGEMENT FUNCTIONS
  // ============================================================================
  
  // Note: This context provides base filter functionality.
  // ProjectFilterContext and CollectionContext extend this with entity-specific features.
  
  // Function to clear all active filters and reset to default view
  const clearAllFilters = useCallback(() => {
    // Clear all search fields
    setSearchByKeyword("");
    setSearchByTitle("");
    setSearchByDescription("");
    setSearchByTag("");
    
    // Clear all selection-based filters
    setSelectedStatuses([]);
    setSelectedTags([]);
    setSelectedVisibilities([]);
    
    // Reset date filter mode and clear date fields
    setDateFilterMode("any");
    setQueryFromDay("");
    setQueryToDay("");
    setIsDateFilterActive(false);
  }, []);

  // Function to clear search filters only
  const clearSearchFilters = useCallback(() => {
    setSearchByKeyword("");
    setSearchByTitle("");
    setSearchByDescription("");
    setSearchByTag("");
  }, []);

  // Function to clear status filters only
  const clearStatusFilters = useCallback(() => {
    setSelectedStatuses([]);
  }, []);

  // Function to clear visibility filters only
  const clearVisibilityFilters = useCallback(() => {
    setSelectedVisibilities([]);
  }, []);

  // Function to clear tag filters only
  const clearTagFilters = useCallback(() => {
    setSelectedTags([]);
  }, []);

  // Function to clear date filters only
  const clearDateFilters = useCallback(() => {
    setQueryFromDay("");
    setQueryToDay("");
    setDateFilterMode("any");
    setIsDateFilterActive(false);
  }, []);

  // ============================================================================
  // SEARCH FUNCTIONS
  // ============================================================================
  
  const updateSearchByTitle = useCallback((value) => {
    setSearchByTitle(value);
  }, []);

  const updateSearchByDescription = useCallback((value) => {
    setSearchByDescription(value);
  }, []);

  const updateSearchByTag = useCallback((value) => {
    setSearchByTag(value);
  }, []);

  const updateSearchByKeyword = useCallback((value) => {
    setSearchByKeyword(value);
  }, []);

  // ============================================================================
  // STATUS FILTER FUNCTIONS
  // ============================================================================
  
  const toggleStatusSelection = useCallback((status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  }, []);

  const updateSelectedStatuses = useCallback((statuses) => {
    setSelectedStatuses(statuses);
  }, []);

  // ============================================================================
  // VISIBILITY FILTER FUNCTIONS
  // ============================================================================
  
  const toggleVisibilitySelection = useCallback((visibilityType) => {
    setSelectedVisibilities(prev => {
      // Toggle behavior (like statuses) for exclusion logic
      if (prev.includes(visibilityType)) {
        return prev.filter(v => v !== visibilityType);
      } else {
        return [...prev, visibilityType];
      }
    });
  }, []);

  const updateSelectedVisibilities = useCallback((visibilities) => {
    setSelectedVisibilities(visibilities);
  }, []);

  // ============================================================================
  // TAG FILTER FUNCTIONS
  // ============================================================================
  
  const toggleTagSelection = useCallback((tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  }, []);

  const updateSelectedTags = useCallback((tags) => {
    setSelectedTags(tags);
  }, []);

  // ============================================================================
  // DATE FILTER FUNCTIONS
  // ============================================================================
  
  const updateDateFilterMode = useCallback((mode) => {
    setDateFilterMode(mode);
    if (mode === "exact") {
      // When switching to exact mode, clear the "to" date and set it equal to "from"
      if (queryFromDay) {
        setQueryToDay(queryFromDay);
      }
    }
  }, [queryFromDay]);

  const updateQueryFromDay = useCallback((date) => {
    setQueryFromDay(date);
    // If in exact mode, also update the "to" date to match
    if (dateFilterMode === "exact") {
      setQueryToDay(date);
    }
  }, [dateFilterMode]);

  const updateQueryToDay = useCallback((date) => {
    setQueryToDay(date);
  }, []);

  const setDateFilterActive = useCallback((active) => {
    setIsDateFilterActive(active);
  }, []);

  // ============================================================================
  // FILTER STATE DETECTION
  // ============================================================================
  
  // Effect to update hasActiveFilters state whenever filter conditions change
  useEffect(() => {
    const isCurrentlyFiltering = 
      searchByKeyword !== "" ||
      searchByTitle !== "" || 
      searchByDescription !== "" || 
      searchByTag !== "" || 
      selectedStatuses.length > 0 ||
      selectedTags.length > 0 ||
      selectedVisibilities.length > 0 ||
      queryFromDay !== "" ||
      queryToDay !== "";
    
    setHasActiveFilters(isCurrentlyFiltering);
  }, [searchByKeyword, searchByTitle, searchByDescription, searchByTag, selectedStatuses, selectedTags, selectedVisibilities, queryFromDay, queryToDay]);

  // ============================================================================
  // CONTEXT VALUE CREATION
  // ============================================================================
  
  const contextValue = useMemo(() => ({
    // ============================================================================
    // FILTER STATE
    // ============================================================================
    searchByKeyword,
    searchByTitle,
    searchByDescription,
    searchByTag,
    dateFilterMode,
    queryFromDay,
    queryToDay,
    isDateFilterActive,
    selectedStatuses,
    selectedTags,
    selectedVisibilities,
    hasActiveFilters,
    entityType,
    
    // ============================================================================
    // FILTER MANAGEMENT FUNCTIONS
    // ============================================================================
    clearAllFilters,
    clearSearchFilters,
    clearStatusFilters,
    clearVisibilityFilters,
    clearTagFilters,
    clearDateFilters,
    
    // ============================================================================
    // SEARCH FUNCTIONS
    // ============================================================================
    setSearchByKeyword: updateSearchByKeyword,
    setSearchByTitle: updateSearchByTitle,
    setSearchByDescription: updateSearchByDescription,
    setSearchByTag: updateSearchByTag,
    
    // ============================================================================
    // STATUS FILTER FUNCTIONS
    // ============================================================================
    setSelectedStatuses: updateSelectedStatuses,
    toggleStatusSelection,
    
    // ============================================================================
    // VISIBILITY FILTER FUNCTIONS
    // ============================================================================
    setSelectedVisibilities: updateSelectedVisibilities,
    toggleVisibilitySelection,
    
    // ============================================================================
    // TAG FILTER FUNCTIONS
    // ============================================================================
    setSelectedTags: updateSelectedTags,
    toggleTagSelection,
    
    // ============================================================================
    // DATE FILTER FUNCTIONS
    // ============================================================================
    setDateFilterMode: updateDateFilterMode,
    setQueryFromDay: updateQueryFromDay,
    setQueryToDay: updateQueryToDay,
    setIsDateFilterActive: setDateFilterActive,
  }), [
    searchByKeyword,
    searchByTitle,
    searchByDescription,
    searchByTag,
    dateFilterMode,
    queryFromDay,
    queryToDay,
    isDateFilterActive,
    selectedStatuses,
    selectedTags,
    selectedVisibilities,
    hasActiveFilters,
    entityType,
    clearAllFilters,
    clearSearchFilters,
    clearStatusFilters,
    clearVisibilityFilters,
    clearTagFilters,
    clearDateFilters,
    updateSearchByKeyword,
    updateSearchByTitle,
    updateSearchByDescription,
    updateSearchByTag,
    updateSelectedStatuses,
    toggleStatusSelection,
    updateSelectedVisibilities,
    toggleVisibilitySelection,
    updateSelectedTags,
    toggleTagSelection,
    updateDateFilterMode,
    updateQueryFromDay,
    updateQueryToDay,
    setDateFilterActive,
  ]);

  return (
    <BaseFilterContext.Provider value={contextValue}>
      {children}
    </BaseFilterContext.Provider>
  );
};
